import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, UserSearch, Printer } from "lucide-react";

const Stat = ({ label, value, tone, testid }) => (
  <div className="border border-slate-200 p-3" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-1 font-display text-2xl font-bold ${tone === "danger" ? "text-red-800" : "text-slate-900"}`}>{value}</div>
  </div>
);

export default function PatientHistory() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [history, setHistory] = useState(null);
  const mrn = params.get("mrn") || "";
  const name = params.get("name") || "";

  const search = useCallback(async () => {
    try {
      const { data } = await api.get("/patients", { params: { q } });
      setPatients(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [q]);

  useEffect(() => { search(); }, [search]);

  useEffect(() => {
    if (!mrn && !name) { setHistory(null); return; }
    api.get("/patients/history", { params: { mrn, name } })
      .then((r) => setHistory(r.data))
      .catch((e) => { setHistory(null); toast.error(apiError(e)); });
  }, [mrn, name]);

  const openPatient = (p) => {
    setParams(p.patient_mrn ? { mrn: p.patient_mrn } : { name: p.patient_name });
  };

  return (
    <div className="space-y-5" data-testid="patient-history-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Patient History & QA Correlation</h1>
        <p className="mt-1 text-sm text-slate-600">
          Every case, block, slide and send-out for a patient in one chronological record — for pathologist correlation and QA review.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <div className="border border-slate-200">
          <div className="border-b border-slate-200 bg-slate-50 p-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-3.5 text-slate-400" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient name or MRN…"
                data-testid="patient-search-input" className="h-11 border-slate-300 bg-white pl-9"
              />
            </div>
          </div>
          <div className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto" data-testid="patient-list">
            {patients.map((p) => {
              const active = (mrn && p.patient_mrn === mrn) || (!mrn && name && p.patient_name === name);
              return (
                <button
                  key={p.key} onClick={() => openPatient(p)} data-testid={`patient-item-${p.patient_mrn || p.patient_name}`}
                  className={`block w-full px-3 py-2.5 text-left row-hover hover:bg-slate-50 ${active ? "bg-[#134e4a]/5 border-l-2 border-[#134e4a]" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900">{p.patient_name}</span>
                    <span className="num text-xs text-slate-500">{p.case_count} case(s)</span>
                  </div>
                  <div className="num text-xs text-slate-500">{p.patient_mrn || "no MRN"} · last {fmtDate(p.last_received_at)}</div>
                  <div className="truncate text-xs text-slate-500">{p.specimen_types.join(", ") || "—"}</div>
                </button>
              );
            })}
            {patients.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="patient-list-empty">No patients match that search.</div>
            )}
          </div>
        </div>

        {!history ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center border border-dashed border-slate-300 p-10 text-center" data-testid="patient-history-empty">
            <UserSearch size={28} className="text-slate-400" />
            <div className="mt-3 font-display text-lg font-semibold text-slate-800">Select a patient</div>
            <p className="mt-1 max-w-sm text-sm text-slate-600">
              Pick a patient on the left to pull every prior case, diagnosis, block, slide and send-out for correlation.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="patient-history-detail">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="history-patient-name">
                  {history.patient.patient_name}
                </h2>
                <div className="num text-sm text-slate-600">
                  MRN {history.patient.patient_mrn || "—"} · DOB {history.patient.patient_dob || "—"} ·
                  history {fmtDate(history.qa.first_case_at)} → {fmtDate(history.qa.last_case_at)}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Pathologists: {history.qa.pathologists.join(", ") || "—"} · Specimen types: {history.qa.specimen_types.join(", ") || "—"}
                </div>
              </div>
              <Button variant="outline" onClick={() => window.print()} data-testid="print-history-button" className="h-11 no-print">
                <Printer size={15} className="mr-2" /> Print record
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6" data-testid="patient-qa-metrics">
              <Stat label="Cases" value={history.qa.case_count} testid="qa-case-count" />
              <Stat label="Open" value={history.qa.open_cases} testid="qa-open-cases" />
              <Stat label="STAT" value={history.qa.stat_cases} testid="qa-stat-cases" />
              <Stat label="Avg TAT" value={`${history.qa.avg_tat_hours}h`} testid="qa-avg-tat" />
              <Stat label="TAT outliers" value={history.qa.tat_outliers} tone={history.qa.tat_outliers ? "danger" : ""} testid="qa-tat-outliers" />
              <Stat label="Blocks / slides" value={`${history.qa.blocks}/${history.qa.slides}`} testid="qa-specimen-counts" />
            </div>

            <Tabs defaultValue="cases">
              <TabsList data-testid="history-tabs">
                <TabsTrigger value="cases" data-testid="history-tab-cases">Cases ({history.cases.length})</TabsTrigger>
                <TabsTrigger value="specimens" data-testid="history-tab-specimens">Blocks & slides ({history.specimens.length})</TabsTrigger>
                <TabsTrigger value="sendouts" data-testid="history-tab-sendouts">Send-outs ({history.sendouts.length})</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="history-tab-timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="cases" className="mt-3">
                <div className="space-y-3" data-testid="history-cases-list">
                  {history.cases.map((c) => (
                    <div key={c.id} className="border border-slate-200 p-3" data-testid={`history-case-${c.accession_number}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/cases/${c.id}`} className="font-mono text-sm font-bold text-[#134e4a] hover:underline">{c.accession_number}</Link>
                        {c.priority === "STAT" && <span className="bg-red-800 px-1.5 text-[11px] font-bold text-white">STAT</span>}
                        <span className="border border-[#134e4a]/30 bg-[#134e4a]/5 px-1.5 text-xs font-semibold text-[#134e4a]">{c.status}</span>
                        <span className="num text-xs text-slate-500">
                          {c.specimen_type || "—"} / {c.specimen_site || "—"} · received {fmtDateTime(c.received_at)} · TAT {c.tat_hours}h
                          {c.tat_hours > history.qa.tat_target_hours && (
                            <span className="ml-1 font-semibold text-red-800">
                              {c.signed_out_at ? "over target" : "in flight, past target"}
                            </span>
                          )}
                        </span>
                        <span className="ml-auto text-xs text-slate-500">{c.pathologist_name || "Unassigned"}</span>
                      </div>
                      <div className="mt-2 grid gap-2 text-sm md:grid-cols-2">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Diagnosis</div>
                          <div className="whitespace-pre-wrap text-slate-800">{c.diagnosis || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gross description</div>
                          <div className="whitespace-pre-wrap text-slate-700">{c.gross_description || "—"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="specimens" className="mt-3">
                <div className="overflow-x-auto border border-slate-200">
                  <table className="w-full min-w-[760px] text-sm" data-testid="history-specimens-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-2">Barcode</th><th className="px-3 py-2">Case</th><th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Stain</th><th className="px-3 py-2">Location</th><th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.specimens.map((s) => (
                        <tr key={s.id} className="border-b border-slate-100" data-testid={`history-specimen-${s.barcode}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{s.barcode}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{s.accession_number}</td>
                          <td className="px-3 py-2 capitalize">{s.kind}</td>
                          <td className="px-3 py-2">{s.stain || "—"}</td>
                          <td className="px-3 py-2 num text-slate-600">{[s.location_rack, s.location_shelf, s.location_box].filter(Boolean).join(" / ") || "—"}</td>
                          <td className="px-3 py-2">{s.checked_out ? `Out · ${s.checked_out_to}` : "In inventory"}</td>
                        </tr>
                      ))}
                      {history.specimens.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">No blocks or slides.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="sendouts" className="mt-3">
                <div className="overflow-x-auto border border-slate-200">
                  <table className="w-full min-w-[860px] text-sm" data-testid="history-sendouts-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-2">Case</th><th className="px-3 py-2">Destination</th><th className="px-3 py-2">Test</th><th className="px-3 py-2">CPT</th>
                        <th className="px-3 py-2">Authorized</th><th className="px-3 py-2">Billing</th>
                        <th className="px-3 py-2">Results</th><th className="px-3 py-2">Materials</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.sendouts.map((s) => (
                        <tr key={s.id} className="border-b border-slate-100" data-testid={`history-sendout-${s.id}`}>
                          <td className="px-3 py-2 font-mono text-slate-600">{s.accession_number}</td>
                          <td className="px-3 py-2 font-medium">{s.destination_lab}</td>
                          <td className="px-3 py-2">{s.test_requested}</td>
                          <td className="px-3 py-2 font-mono text-slate-700">{s.cpt_codes || "—"}</td>
                          <td className="px-3 py-2">{s.authorized_by_name || "—"}</td>
                          <td className="px-3 py-2">{s.billing_status} · {money(s.charge_amount)}</td>
                          <td className="px-3 py-2">{s.results_received ? s.results_summary || "Received" : "Pending"}</td>
                          <td className="px-3 py-2">{s.materials_returned ? "Returned" : "Outstanding"}</td>
                        </tr>
                      ))}
                      {history.sendouts.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No send-outs.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="mt-3">
                <ol className="border-l-2 border-slate-200 pl-5" data-testid="history-timeline">
                  {history.events.map((e) => (
                    <li key={e.id} className="relative pb-4" data-testid={`history-event-${e.id}`}>
                      <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-[#134e4a]" />
                      <div className="text-sm font-semibold text-slate-900">
                        <span className="font-mono text-[#134e4a]">{e.accession_number}</span> · {e.status}
                      </div>
                      <div className="num text-xs text-slate-500">{fmtDateTime(e.at)} · {e.user_name}</div>
                      {e.note && <div className="text-sm text-slate-700">{e.note}</div>}
                    </li>
                  ))}
                </ol>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}
